import * as React            from "react";
import {UseState}            from "@Common/react/use-state";

import {cs}                  from "@Common/react/chain-services";
import {consumeContext}      from "@Common/react/context";
import {Load2}               from "@Common/react/load2";
import {buildUrlQuery}       from "@Common/utils/build-url-query";
import {BuildPathHandler}    from "./build-path-handler";

export const OrderBuildManager = ({item, itemSku, orderId, session, isListingsEligible, match, routerHistory, query, setBuild3State, next}) => cs(

    consumeContext("templateOptions"),
    consumeContext("apis"),
    ["buildPath", ({templateOptions}, next) => next(templateOptions.build_path)],
    ["contentFields", ({apis}, next) => Load2({
        fetch: async () => !orderId ? null : await apis.next.order.getContentFields(orderId, itemSku, match.params.component_id),
        next,
    })],
    ["specificationFields", ({apis}, next) => Load2({
        fetch: async () => !orderId ? null : await apis.next.order.getSpecFields(orderId, itemSku, match.params.component_id),
        next,
    })],
    ["commonGenericBuildPaths", ({}, next) =>  next(
        [
            "cbfl_gen_buscard_v2.inc",
            "gen_buscard_v2.inc",
            "headshot_not_required.inc",
        ]
    )],
    ["orderData", ({apis, buildPath, contentFields, specificationFields}, next) => Load2({
        fetch: async () => {
            let listingFromApi = null;
            if (item.listingUniqueId) {
                listingFromApi = await apis.listings.getListing({idUnique: item.listingUniqueId});
            }
            await apis.next.OrderManager.createOrderDataEntry(item.itemId,
                {
                ...itemSku,
                idCompany: item.idCompany,
                idBranch: item.idBranch,
                idAgent: item.idAgent
                },
                match.params.component_id
            );
            let orderData = await apis.next.OrderManager.getOrderData(item.itemId, match.params.component_id);
            await contentFields.reload();
            const updatedSpecFields = await specificationFields.reload();
            if (orderData?.length > 0) {
                if(query?.reset === "branch"){
                    orderData = await apis.next.OrderManager.updateBranchAgentOrderData(item?.itemId, item.idCompany, item.idBranch, item.idAgent, match.params.component_id);
                }
                else if(query?.reset === "agent"){
                    orderData = await apis.next.OrderManager.updateBranchAgentOrderData(item?.itemId, item.idCompany, "false", item.idAgent, match.params.component_id);
                }
            }
            if (isListingsEligible && listingFromApi && Object.keys(listingFromApi).length !== 0) {
                const idComponent = match.params.component_id ?? null;

                orderData = await apis.next.OrderManager.applyListingData(item?.itemId, listingFromApi, idComponent);

                let listingPhotos = [];
                let galleryPhotos = await apis.next.gallery.orders.getImages(item?.itemId);

                listingFromApi?.pictures.map( (image, index) => {
                    let name = image.caption || `${listingFromApi.location.streetAddress} Photo ${index+1}`;
                    listingPhotos.push({name: name, url: image.pictureUrl});
                })
                if ( !galleryPhotos.find(photo => photo.name == listingPhotos[0].name) ) {
                    await apis.next.gallery.listings.saveImages({
                        listingId: listingFromApi?._id,
                        remoteImages: JSON.stringify(listingPhotos),
                        orderId: item?.itemId,
                    });
                }
            }
            const buildpathData = BuildPathHandler({
                buildPath,
                idBranch: item.idBranch,
                session,
                contentFields,
                specificationFields: updatedSpecFields.sort((a, b) => a.ordinal - b.ordinal),
                specFieldOnChange: (val)=> specificationFields.onChange(val),
                orderData,
                next
            });
            setBuild3State({updatedOrderData: orderData});
            return orderData;
        },
        next,
    })],
    ["allImageFields", ({apis}, next) => Load2({
        fetch: async () => {
            if(query?.reset){
                await apis.next.OrderManager.updateAgentHeadshots(item?.itemId, match.params.component_id);
                delete query.reset;
                routerHistory.push(`/customize/order/${item.itemId}` + (match.params.component_id? `/${match.params.component_id}`:'') + buildUrlQuery(query));
            }
            let imageFields = await apis.next.OrderManager.getOrderImageFields(item.itemId, match.params.component_id);
            if (!imageFields?.length) {
                await apis.next.OrderManager.createOrderImageEntries(item.itemId, {...itemSku}, match.params.component_id);
                return await apis.next.OrderManager.getOrderImageFields(item.itemId, match.params.component_id);
            }
            return imageFields;
        },
        next
    })],
    ["templateDefinitionImg", ({apis,buildPath,commonGenericBuildPaths}, next) => Load2({
        fetch: async () => (!commonGenericBuildPaths.includes(buildPath)) ? [] : await apis.next.OrderManager.getTemplateDefiImages(itemSku?.front, "photo"),
        next,
    })],
    ["headshotFields", ({allImageFields}, next) => next(allImageFields.value?.filter( image => {
        return image.idContent.indexOf("headshot") >= 0;
    }))],
    ["photoFields", ({allImageFields, buildPath, templateDefinitionImg}, next) => UseState({
        _key: allImageFields,
        initValue: (buildPath === "cbfl_gen_buscard_v2.inc") ?
            allImageFields.value?.filter(image =>  image.idContent.indexOf("photo") >= 0 && image.label.indexOf("QR") < 0)
                .slice(templateDefinitionImg['value']?.length, templateDefinitionImg['value']?.length+1)
                .map((image, index) => ({
                    ...image,
                    label: `Photo ${index + 1}`,
                    idContent: `photo_0${index + 1}`
                  }))
        : buildPath === "gen_buscard_v2.inc" || buildPath === "headshot_not_required.inc" ?
            []
        :
            allImageFields.value?.filter(image => {
                return image.idContent.indexOf("photo") >= 0 && image.label.indexOf("QR") < 0;
            }),
        next
    })],
    ["logoFields", ({allImageFields, buildPath, templateDefinitionImg,commonGenericBuildPaths}, next) => UseState({
        _key: allImageFields,
        initValue: buildPath === "cbfl_gen_buscard_v2.inc"
        ?
            allImageFields.value?.filter( image => {
                return (image.idContent.indexOf("photo") >= 0 && image.label.indexOf("QR") < 0);
            }).slice(0, templateDefinitionImg['value']?.length)
        : buildPath === "gen_buscard_v2.inc" || buildPath === "headshot_not_required.inc" ?
            allImageFields.value?.filter( image => {
                return (image.idContent.indexOf("photo") >= 0 && image.label.indexOf("QR") < 0);
            })
        :
            allImageFields.value?.filter(image => {
                return image.idContent.indexOf("logo") >= 0;
            }),

        next
    })],

    ["qrCodeFields", ({allImageFields, buildPath, templateDefinitionImg,commonGenericBuildPaths}, next) => next(
        commonGenericBuildPaths.includes(buildPath)
        ?
        allImageFields.value?.filter(image => image.label.indexOf("QR") >= 0)
        .slice(0, templateDefinitionImg['value']?.length+1)
        .map((image, index) => ({
            ...image,
            label: `QR Code ${index + 1}`,
            idContent: `qr_photo_0${index + 1}`
            }))
        :
        allImageFields.value?.filter(image => image.label.indexOf("QR") >= 0)
        .map((image, index) => ({
            ...image,
            label: `QR Code ${index + 1}`,
            idContent: `qr_photo_0${index + 1}`
            }))
    )],

    ["designations", ({apis}, next) => Load2({
        fetch: async () => {
            let backDesignations = await apis.next.gallery.designations.getImages(itemSku.back, {isBuild3Order: true});
            let frontDesignations = await apis.next.gallery.designations.getImages(itemSku.front, {isBuild3Order: true});
            if (backDesignations.length) return backDesignations;
            else if (frontDesignations.length) return frontDesignations;
            return false
        },
        captureException: true,
        next,
    })],
    ["userDisplayFields", ({orderData}, next) => next(
        orderData.value?.filter( (datapoint) => datapoint.idContent.includes("display_agent") || datapoint.label.includes("License Number") )
    )],
    ["branchDisplayFields", ({orderData}, next) => next(
        orderData.value?.filter( (datapoint) => datapoint.idContent.includes("display_branch") )
    )],
    ["loading", (({orderData, allImageFields, designations, contentFields, specificationFields}, next) => next(
        [orderData, allImageFields, designations, contentFields, specificationFields].every(
            data => data.loading === false) ? false : true
    ))],

    ({allImageFields, headshotFields, photoFields, logoFields, qrCodeFields, designations, userDisplayFields, branchDisplayFields, contentFields, specificationFields, loading}) => next({
        loading,
        imageFields: allImageFields,
        headshotFields,
        photoFields: photoFields.value,
        logoFields: logoFields.value,
        qrCodeFields,
        designations,
        userDisplayFields,
        branchDisplayFields,
        contentFields,
        specificationFields,
    }),
);